import { useState, useEffect, useRef } from 'react';
import { adminUsersAPI } from '../../lib/admin-api';

interface User {
    id: number;
    username: string;
    email: string;
    display_name?: string;
    profile_picture?: string;
}

interface UserSearchInputProps {
    label: string;
    onSelect: (user: User | null) => void;
    selectedUser?: User | null;
    placeholder?: string;
}

export default function UserSearchInput({ label, onSelect, selectedUser, placeholder }: UserSearchInputProps) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Update query if selectedUser changes externally (e.g. reset form)
    useEffect(() => {
        if (selectedUser) {
            setQuery(`${selectedUser.username} (${selectedUser.email})`);
        } else {
            setQuery('');
        }
    }, [selectedUser]);

    const handleSearch = (value: string) => {
        setQuery(value);

        // If user clears input, clear selection
        if (value.trim() === '') {
            onSelect(null);
            setResults([]);
            setShowDropdown(false);
            return;
        }

        // Debounce search
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        setLoading(true);
        searchTimeoutRef.current = setTimeout(async () => {
            try {
                const response = await adminUsersAPI.getUsers({
                    search: value,
                    limit: 5, // Limit results for dropdown
                    page: 1
                });
                setResults(response.users);
                setShowDropdown(true);
            } catch (error) {
                console.error('User search failed:', error);
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 300);
    };

    const handleSelect = (user: User) => {
        onSelect(user);
        setQuery(`${user.username} (${user.email})`);
        setShowDropdown(false);
    };

    return (
        <div className="form-control mb-4" ref={dropdownRef}>
            <label className="label">
                <span className="label-text">{label}</span>
            </label>
            <div className="relative">
                <input
                    type="text"
                    className={`input input-bordered w-full ${loading ? 'input-primary' : ''}`}
                    value={query}
                    onChange={(e) => handleSearch(e.target.value)}
                    onFocus={() => {
                        if (results.length > 0) setShowDropdown(true);
                    }}
                    placeholder={placeholder || "Search by username, email..."}
                />
                {loading && (
                    <div className="absolute right-3 top-3">
                        <span className="loading loading-spinner loading-xs"></span>
                    </div>
                )}

                {showDropdown && results.length > 0 && (
                    <ul className="absolute z-10 menu p-2 shadow bg-base-100 rounded-box w-full mt-1 max-h-60 overflow-y-auto border border-base-200">
                        {results.map((user) => (
                            <li key={user.id}>
                                <a onClick={() => handleSelect(user)} className="flex flex-col items-start gap-0">
                                    <span className="font-bold">{user.username}</span>
                                    <span className="text-xs opacity-70">{user.email}</span>
                                    {user.display_name && <span className="text-xs opacity-50">({user.display_name})</span>}
                                </a>
                            </li>
                        ))}
                    </ul>
                )}

                {showDropdown && results.length === 0 && query.length > 1 && !loading && (
                    <div className="absolute z-10 p-4 shadow bg-base-100 rounded-box w-full mt-1 border border-base-200 text-center text-sm opacity-70">
                        No users found
                    </div>
                )}
            </div>
        </div>
    );
}
